/**
 * Bridge between the web app and the native shell that wraps it.
 *
 * Why this exists: Google refuses OAuth inside an embedded WebView
 * (`disallowed_useragent`), so `signInWithPopup` and `signInWithRedirect` both
 * fail in the packaged app. The shell therefore performs Google Sign-In
 * natively, where Google does allow it, and hands the resulting ID token back
 * through here. Firebase then accepts it via `signInWithCredential`, producing
 * exactly the same session the browser would have created.
 *
 * In a normal browser none of this runs — `isNativeShell()` is false and the
 * ordinary popup/redirect path is used.
 */

export type NativeMessage =
  | { type: 'google-signin' }
  | { type: 'google-signout' }
  | { type: 'apple-signin' }
  | { type: 'speech-start' }
  | { type: 'speech-restart' }
  | { type: 'speech-stop' }
  | { type: 'ensure-camera' };

/**
 * What the shell hands back after a native Sign in with Apple.
 *
 * `rawNonce` is not optional. Apple signs the SHA-256 of the nonce into the
 * identity token and Firebase re-hashes the raw value to check it matches, so
 * a credential built without it is rejected as unverifiable. Only the shell can
 * supply it, because that is where the hash was computed.
 *
 * `displayName` arrives exactly once, on the first authorisation for this Apple
 * ID. Apple never sends it again, so an account that does not record it here
 * keeps whatever it had — usually nothing.
 */
export interface NativeAppleCredential {
  identityToken: string;
  rawNonce: string;
  displayName?: string | null;
}

/** What the shell says it can do. Absent in a browser. */
export interface NativeCaps {
  speech: boolean;
  camera: boolean;
}

export interface NativeSpeechEvent {
  event: 'start' | 'result' | 'error' | 'end';
  transcript?: string;
  interim?: string;
  isFinal?: boolean;
  code?: string;
  message?: string;
}

export interface NativeCameraResult {
  granted: boolean;
  canAskAgain?: boolean;
  error?: string;
}

interface NativeWindow extends Window {
  /** Set by the shell before page load. */
  __BLISX_NATIVE__?: boolean;
  /** Installed by useAuth; the shell calls it with a Google ID token. */
  __blisxNativeAuth?: (idToken: string) => void;
  /** Installed by useAuth; the shell calls it with an Apple identity token. */
  __blisxNativeAppleAuth?: (json: string) => void;
  /** Installed by useAuth; the shell calls it when native sign-in fails. */
  __blisxNativeAuthError?: (message: string) => void;
  __BLISX_NATIVE_CAPS__?: NativeCaps;
  /** Installed by useSpeech; the shell streams recognition events here. */
  __blisxNativeSpeech?: (json: string) => void;
  /** Installed by the capture bar; the shell reports the camera decision here. */
  __blisxNativeCamera?: (json: string) => void;
  ReactNativeWebView?: { postMessage: (data: string) => void };
}

const w = () => window as NativeWindow;

/** True only inside the Expo shell, which injects the flag before load. */
export function isNativeShell(): boolean {
  return typeof window !== 'undefined' && w().__BLISX_NATIVE__ === true;
}

/**
 * True inside the App Store build only. That is the one place credits must not
 * be sold or pointed at (App Store guideline 3.1.1). The Android app is a
 * sideloaded APK, outside any store's billing rules, so it sells like the web.
 * Read off the user agent so the APKs already installed pick this up without
 * a rebuild.
 */
export function isIosShell(): boolean {
  return isNativeShell() && !/Android/i.test(navigator.userAgent);
}

export function nativeCaps(): NativeCaps | null {
  return typeof window === 'undefined' ? null : (w().__BLISX_NATIVE_CAPS__ ?? null);
}

/**
 * Ask the shell for camera permission and wait for the answer.
 * Android refuses the capture intent when CAMERA is declared but not granted,
 * so the picker must not open until this resolves. Outside the shell there is
 * nothing to ask — the browser prompts on its own.
 */
export function requestNativeCamera(timeoutMs = 20_000): Promise<NativeCameraResult> {
  if (!isNativeShell()) return Promise.resolve({ granted: true });
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: NativeCameraResult) => {
      if (settled) return;
      settled = true;
      delete w().__blisxNativeCamera;
      resolve(result);
    };
    w().__blisxNativeCamera = (json) => {
      try {
        finish(JSON.parse(json) as NativeCameraResult);
      } catch {
        finish({ granted: false });
      }
    };
    if (!postToNative({ type: 'ensure-camera' })) finish({ granted: true });
    // A shell that never answers must not freeze the capture button.
    window.setTimeout(() => finish({ granted: true }), timeoutMs);
  });
}

export function onNativeSpeech(handler: (e: NativeSpeechEvent) => void): () => void {
  w().__blisxNativeSpeech = (json) => {
    try {
      handler(JSON.parse(json) as NativeSpeechEvent);
    } catch {
      // Ignore anything we cannot parse.
    }
  };
  return () => {
    delete w().__blisxNativeSpeech;
  };
}

export function postToNative(message: NativeMessage): boolean {
  const bridge = w().ReactNativeWebView;
  if (!bridge) return false;
  bridge.postMessage(JSON.stringify(message));
  return true;
}

/**
 * Register the callbacks the shell invokes. Returns a cleanup function.
 * Kept separate from the shell detection so the handlers exist even if the
 * flag arrives late.
 */
export function registerNativeAuthHandlers(handlers: {
  onIdToken: (idToken: string) => void;
  onAppleCredential: (credential: NativeAppleCredential) => void;
  onError: (message: string) => void;
}): () => void {
  w().__blisxNativeAuth = handlers.onIdToken;
  w().__blisxNativeAppleAuth = (json) => {
    try {
      const parsed = JSON.parse(json) as NativeAppleCredential;
      if (!parsed?.identityToken || !parsed?.rawNonce) {
        // Half a credential cannot be exchanged, and failing quietly here
        // leaves the button spinning with nothing to report.
        handlers.onError('Apple sign-in returned an incomplete credential.');
        return;
      }
      handlers.onAppleCredential(parsed);
    } catch {
      handlers.onError('Apple sign-in returned something unreadable.');
    }
  };
  w().__blisxNativeAuthError = handlers.onError;
  return () => {
    delete w().__blisxNativeAuth;
    delete w().__blisxNativeAppleAuth;
    delete w().__blisxNativeAuthError;
  };
}
